/**
 * useShoppingList — TanStack Query wrapper around `GET /api/shopping/items`
 * with grouping/sort logic baked in (Plan 04-03 Wave-2 data layer).
 *
 * Returns `{ ...query, grouped }` where `grouped` is the ready-to-render
 * structure used by `<ShoppingRoute>`:
 *
 *   - `sections[]`: an ordered list of `{ key, items }`, with `key` being one
 *     of the 8 D-03 `CategoryKey` values OR `'ukategorisert'` (for rows where
 *     `category IS NULL`). Sections with zero unchecked items are omitted
 *     entirely (UI-SPEC §"<CategorySection> Empty section: hide the header").
 *     Display order follows `CATEGORY_KEYS` (CATEGORY_ORDER) verbatim;
 *     `ukategorisert` is appended last among real sections.
 *
 *   - `checked[]`: every checked row, sorted by `checked_at DESC` per D-12.
 *     Rendered inside the `<Haket av (N)>` collapsible group at the bottom.
 *
 * Within each unchecked section the rows are sorted by `sort_order` ASC,
 * matching the server-side INSERT-at-MAX(sort_order)+1 invariant (D-13).
 *
 * D-09 cache: 60-second `staleTime` keeps the list snappy without spamming
 * GETs as the user toggles between routes; the WS subscriber
 * (`useShoppingWS`) writes the cache directly so changes from peer sessions
 * arrive without a refetch.
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  CATEGORY_KEYS,
  type CategoryKey,
  type ShoppingItem,
} from '@stell/shared';

export type GroupedSectionKey = CategoryKey | 'ukategorisert';

export interface GroupedSection {
  key: GroupedSectionKey;
  items: ShoppingItem[];
}

export interface GroupedShoppingList {
  sections: GroupedSection[];
  checked: ShoppingItem[];
}

/**
 * Optimistic-cache row shape — extends the canonical ShoppingItem with a
 * transient `_pending` flag that useShoppingMutations.addItem.onMutate sets
 * (Plan 04-04 D-09). The route reads pendingIds via this projection so the
 * <OfflinePendingChip> renders during the offline window.
 */
type OptimisticItem = ShoppingItem & { _pending?: boolean };

async function fetchShoppingList(): Promise<ShoppingItem[]> {
  const res = await fetch('/api/shopping/items', { credentials: 'include' });
  if (!res.ok) throw new Error(`shopping_list_${res.status}`);
  const json = (await res.json()) as { items: ShoppingItem[] };
  return json.items;
}

/**
 * Pure grouping function (exported for unit tests). Splits an arbitrary
 * `ShoppingItem[]` into the section-tree the route renders.
 */
export function groupItems(items: ShoppingItem[]): GroupedShoppingList {
  const byCategory = new Map<GroupedSectionKey, ShoppingItem[]>();
  const checked: ShoppingItem[] = [];

  for (const it of items) {
    if (it.checked === 1) {
      checked.push(it);
      continue;
    }
    const key: GroupedSectionKey = (it.category ?? 'ukategorisert') as GroupedSectionKey;
    const arr = byCategory.get(key) ?? [];
    arr.push(it);
    byCategory.set(key, arr);
  }

  // Sort each section by sort_order ASC.
  for (const arr of byCategory.values()) {
    arr.sort((a, b) => a.sort_order - b.sort_order);
  }

  // Sort Haket av by checked_at DESC (most recently ticked at top — D-12).
  checked.sort((a, b) => (b.checked_at ?? '').localeCompare(a.checked_at ?? ''));

  // Build sections in CATEGORY_KEYS order; ukategorisert last.
  const sections: GroupedSection[] = [];
  for (const key of CATEGORY_KEYS) {
    const arr = byCategory.get(key);
    if (arr && arr.length > 0) sections.push({ key, items: arr });
  }
  const uncat = byCategory.get('ukategorisert');
  if (uncat && uncat.length > 0) sections.push({ key: 'ukategorisert', items: uncat });

  return { sections, checked };
}

export function useShoppingList() {
  const q = useQuery({
    queryKey: ['shopping', 'items'],
    queryFn: fetchShoppingList,
    staleTime: 60_000,
  });

  const grouped = useMemo<GroupedShoppingList>(
    () => groupItems(q.data ?? []),
    [q.data],
  );

  // Project the `_pending` flag from optimistic ghost rows into a stable
  // `Set<id>` the route hands down to <CategorySection>/<ItemRow>. Plan 04-04
  // D-09 keeps `_pending: true` on the cache row through the offline window;
  // <OfflinePendingChip> renders against this id set.
  const pendingIds = useMemo<ReadonlySet<string>>(
    () =>
      new Set(
        ((q.data as OptimisticItem[] | undefined) ?? [])
          .filter((it) => it._pending === true)
          .map((it) => it.id),
      ),
    [q.data],
  );

  return { ...q, grouped, pendingIds };
}
